import React, { useState, useEffect, useCallback, FormEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
    Card, CardContent, CardHeader,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
    Paper, Typography, Chip, Switch, FormControlLabel, Tooltip, Stack
} from '@mui/material';
import { Edit, Delete, Add, ContentCopy, Refresh, MenuBook } from '@mui/icons-material';
import Loader from '../Loader';
import { useToast } from '../../hooks/useToast';
import { ToastNotification } from '../common/ToastNotification';
import { formatApiError } from '../../utils/errorFormatter';

interface ApiKeyItem {
    id: number;
    key: string;
    name: string;
    description: string | null;
    allowFullData: boolean;
    isActive: boolean;
    createdAt: string;
    lastUsedAt: string | null;
}

export const ApiKeysPage: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [apiKeys, setApiKeys] = useState<ApiKeyItem[]>([]);

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [createName, setCreateName] = useState('');
    const [createDescription, setCreateDescription] = useState('');
    const [createAllowFullData, setCreateAllowFullData] = useState(false);

    // Edit dialog
    const [editOpen, setEditOpen] = useState(false);
    const [editKey, setEditKey] = useState<ApiKeyItem | null>(null);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editAllowFullData, setEditAllowFullData] = useState(false);
    const [editIsActive, setEditIsActive] = useState(true);

    // Delete dialog
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteKey, setDeleteKey] = useState<ApiKeyItem | null>(null);

    // View key dialog
    const [viewKeyOpen, setViewKeyOpen] = useState(false);
    const [viewKeyValue, setViewKeyValue] = useState('');
    const [viewKeyName, setViewKeyName] = useState('');

    // Regenerate dialog
    const [regenerateOpen, setRegenerateOpen] = useState(false);
    const [regenerateKey, setRegenerateKey] = useState<ApiKeyItem | null>(null);

    const { toast, showError, showSuccess, handleClose } = useToast();

    const fetchApiKeys = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch('/api/apikeys');
            if (!response.ok) {
                const errMsg = await formatApiError(response, 'Ошибка загрузки API ключей');
                showError(errMsg);
                return;
            }
            const data = await response.json();
            setApiKeys(data);
        } catch (e) {
            showError('Ошибка загрузки API ключей');
        } finally {
            setLoading(false);
        }
    }, [showError]);

    useEffect(() => {
        fetchApiKeys();
    }, [fetchApiKeys]);

    const handleCreate = async (e: FormEvent) => {
        e.preventDefault();
        try {
            const response = await fetch('/api/apikeys', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: createName,
                    description: createDescription || null,
                    allowFullData: createAllowFullData
                })
            });
            if (!response.ok) {
                const errMsg = await formatApiError(response, 'Ошибка создания ключа');
                showError(errMsg);
                return;
            }
            const created: ApiKeyItem = await response.json();
            showSuccess('API ключ создан');
            setCreateOpen(false);
            setCreateName('');
            setCreateDescription('');
            setCreateAllowFullData(false);
            // Show the key
            setViewKeyName(created.name);
            setViewKeyValue(created.key);
            setViewKeyOpen(true);
            await fetchApiKeys();
        } catch (e) {
            showError('Ошибка создания ключа');
        }
    };

    const handleEdit = async (e: FormEvent) => {
        e.preventDefault();
        if (!editKey) return;
        try {
            const response = await fetch('/api/apikeys', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: editKey.id,
                    name: editName,
                    description: editDescription || null,
                    allowFullData: editAllowFullData,
                    isActive: editIsActive
                })
            });
            if (!response.ok) {
                const errMsg = await formatApiError(response, 'Ошибка обновления ключа');
                showError(errMsg);
                return;
            }
            showSuccess('API ключ обновлён');
            setEditOpen(false);
            await fetchApiKeys();
        } catch (e) {
            showError('Ошибка обновления ключа');
        }
    };

    const handleDelete = async () => {
        if (!deleteKey) return;
        try {
            const response = await fetch(`/api/apikeys/${deleteKey.id}`, { method: 'DELETE' });
            if (!response.ok) {
                const errMsg = await formatApiError(response, 'Ошибка удаления ключа');
                showError(errMsg);
                return;
            }
            showSuccess('API ключ удалён');
            setDeleteOpen(false);
            await fetchApiKeys();
        } catch (e) {
            showError('Ошибка удаления ключа');
        }
    };

    const handleRegenerate = async () => {
        if (!regenerateKey) return;
        try {
            const response = await fetch(`/api/apikeys/${regenerateKey.id}/regenerate`, { method: 'POST' });
            if (!response.ok) {
                const errMsg = await formatApiError(response, 'Ошибка перегенерации ключа');
                showError(errMsg);
                return;
            }
            const result: ApiKeyItem = await response.json();
            showSuccess('API ключ перегенерирован');
            setRegenerateOpen(false);
            setViewKeyName(result.name);
            setViewKeyValue(result.key);
            setViewKeyOpen(true);
            await fetchApiKeys();
        } catch (e) {
            showError('Ошибка перегенерации ключа');
        }
    };

    const openEditDialog = (key: ApiKeyItem) => {
        setEditKey(key);
        setEditName(key.name);
        setEditDescription(key.description || '');
        setEditAllowFullData(key.allowFullData);
        setEditIsActive(key.isActive);
        setEditOpen(true);
    };

    const openDeleteDialog = (key: ApiKeyItem) => {
        setDeleteKey(key);
        setDeleteOpen(true);
    };

    const openRegenerateDialog = (key: ApiKeyItem) => {
        setRegenerateKey(key);
        setRegenerateOpen(true);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            showSuccess('Скопировано в буфер обмена');
        });
    };

    const truncateKey = (key: string) => {
        if (key.length <= 8) return key;
        return key.substring(0, 8) + '...';
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleString('ru-RU');
    };

    if (loading) return <Loader />;

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            <Card>
                <CardHeader
                    title="API ключи"
                    action={
                        <Stack direction="row" spacing={1}>
                            <Button
                                component={RouterLink}
                                to="/api-docs"
                                variant="outlined"
                                startIcon={<MenuBook />}
                            >
                                Документация API
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => setCreateOpen(true)}
                            >
                                Создать ключ
                            </Button>
                        </Stack>
                    }
                />
                <CardContent>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Название</TableCell>
                                    <TableCell>Ключ</TableCell>
                                    <TableCell>Описание</TableCell>
                                    <TableCell align="center">Полные данные</TableCell>
                                    <TableCell align="center">Активен</TableCell>
                                    <TableCell>Создан</TableCell>
                                    <TableCell>Последнее использование</TableCell>
                                    <TableCell align="right">Действия</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {apiKeys.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <Typography color="textSecondary">Нет API ключей</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    apiKeys.map((k) => (
                                        <TableRow key={k.id}>
                                            <TableCell>{k.name}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                        {truncateKey(k.key)}
                                                    </Typography>
                                                    <Tooltip title="Копировать ключ">
                                                        <IconButton size="small" onClick={() => copyToClipboard(k.key)}>
                                                            <ContentCopy fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                            <TableCell>{k.description || '-'}</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={k.allowFullData ? 'Да' : 'Нет'}
                                                    color={k.allowFullData ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={k.isActive ? 'Активен' : 'Отключён'}
                                                    color={k.isActive ? 'success' : 'error'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{formatDate(k.createdAt)}</TableCell>
                                            <TableCell>{formatDate(k.lastUsedAt)}</TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Редактировать">
                                                    <IconButton size="small" onClick={() => openEditDialog(k)}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Перегенерировать ключ">
                                                    <IconButton size="small" onClick={() => openRegenerateDialog(k)}>
                                                        <Refresh fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Удалить">
                                                    <IconButton size="small" color="error" onClick={() => openDeleteDialog(k)}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Create Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <form onSubmit={handleCreate}>
                    <DialogTitle>Создание API ключа</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Название"
                            fullWidth
                            required
                            value={createName}
                            onChange={(e) => setCreateName(e.target.value)}
                        />
                        <TextField
                            margin="dense"
                            label="Описание"
                            fullWidth
                            multiline
                            rows={2}
                            value={createDescription}
                            onChange={(e) => setCreateDescription(e.target.value)}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={createAllowFullData}
                                    onChange={(e) => setCreateAllowFullData(e.target.checked)}
                                />
                            }
                            label="Разрешить полные данные (RefundCode, ExternalId)"
                            sx={{ mt: 1 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCreateOpen(false)}>Отмена</Button>
                        <Button type="submit" variant="contained">Создать</Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <form onSubmit={handleEdit}>
                    <DialogTitle>Редактирование API ключа</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Название"
                            fullWidth
                            required
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                        />
                        <TextField
                            margin="dense"
                            label="Описание"
                            fullWidth
                            multiline
                            rows={2}
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editAllowFullData}
                                    onChange={(e) => setEditAllowFullData(e.target.checked)}
                                />
                            }
                            label="Разрешить полные данные (RefundCode, ExternalId)"
                            sx={{ mt: 1 }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editIsActive}
                                    onChange={(e) => setEditIsActive(e.target.checked)}
                                />
                            }
                            label="Активен"
                            sx={{ mt: 1 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditOpen(false)}>Отмена</Button>
                        <Button type="submit" variant="contained">Сохранить</Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
                <DialogTitle>Удаление API ключа</DialogTitle>
                <DialogContent>
                    <Typography>
                        Вы уверены, что хотите удалить ключ "{deleteKey?.name}"?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)}>Отмена</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Удалить</Button>
                </DialogActions>
            </Dialog>

            {/* View Key Dialog */}
            <Dialog open={viewKeyOpen} onClose={() => setViewKeyOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>API ключ: {viewKeyName}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        Скопируйте ключ сейчас. В целях безопасности он больше не будет показан полностью.
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                            {viewKeyValue}
                        </Typography>
                        <Tooltip title="Копировать">
                            <IconButton onClick={() => copyToClipboard(viewKeyValue)}>
                                <ContentCopy />
                            </IconButton>
                        </Tooltip>
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewKeyOpen(false)} variant="contained">Закрыть</Button>
                </DialogActions>
            </Dialog>

            {/* Regenerate Confirmation Dialog */}
            <Dialog open={regenerateOpen} onClose={() => setRegenerateOpen(false)}>
                <DialogTitle>Перегенерация API ключа</DialogTitle>
                <DialogContent>
                    <Typography>
                        Вы уверены, что хотите перегенерировать ключ "{regenerateKey?.name}"?
                    </Typography>
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        Старый ключ перестанет работать. Все интеграции, использующие этот ключ, нужно будет обновить.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRegenerateOpen(false)}>Отмена</Button>
                    <Button onClick={handleRegenerate} color="warning" variant="contained">Перегенерировать</Button>
                </DialogActions>
            </Dialog>

            <ToastNotification toast={toast} onClose={handleClose} />
        </Box>
    );
};
